import {
  BackEndValueType,
  DashboardState,
  ECardinality,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  ISymbol
} from "@kuika/kuika-cl-model";
import { EWFAssignmentType, IUserInteraction } from "@kuika/kuika-cl-model/src/models/workflow";
import { Guid } from "guid-typescript";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

export interface ISendWhatsappBusinessMessageSymbolsGroupState {}

export interface ISendWhatsappBusinessMessageSymbolsGroupProps {
  action?: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild, changeReason?: ESdChangeReason) => void;
  interactions: IUserInteraction[];
  changeActionParameter: (parameter: IActionParameter, symbol: ISymbol) => void;
  updateParams: (parameters: IActionParameter[]) => void;
}

class SendWhatsappBusinessMessageSymbolsGroup extends PureSdComponent<
  ISendWhatsappBusinessMessageSymbolsGroupProps & IWithSDProps & IWithContextProp,
  ISendWhatsappBusinessMessageSymbolsGroupState
> {
  constructor(props: ISendWhatsappBusinessMessageSymbolsGroupProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {};
  }

  changeActionParameter = (
    parameterName: string,
    symbol: ISymbol,
    typename: BackEndValueType,
    changeReason?: ESdChangeReason
  ) => {
    const actionParameters = this.props.parameters;
    const actionParameter = actionParameters.find((p) => p.name === parameterName);
    if (actionParameter) {
      actionParameter.symbol = symbol;
    } else {
      actionParameters.push({
        name: parameterName,
        symbol,
        cardinality: ECardinality.One,
        Typename: typename,
        isOptionalParameter: false
      });
    }
    const valuesToUpdate = {
      actionParameters
    } as IActionPanelStatesCanBeUpdatedFromChild;
    this.props.updateValues(valuesToUpdate, changeReason);
  };

  renderSymbolParameter = (parameter: IActionParameter) => {
    return (
      <div key={Guid.create().toString()}>
        <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
          {/* `displayName` is repurposed here to group HEADER/BODY template variables, not to relabel
              this row — pass an explicit object so ActionParameterTitle shows the parameter's own name. */}
          <ActionParameterTitle
            parameter={{ name: parameter.name, Typename: parameter.Typename, cardinality: parameter.cardinality }}
          />
        </span>
        <SymbolPicker
          action={this.props.action}
          changeType="onBlur"
          key={Guid.create().toString()}
          symbol={parameter.symbol}
          valueType={parameter.Typename}
          targetCardinality={parameter.cardinality}
          onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) => {
            this.changeActionParameter(parameter.name, symbol, parameter.Typename, changeReason);
          }}
          bindingParameter={parameter}
          bindingLabel={parameter.name}
        />
      </div>
    );
  };

  addWhatsappBusinessTemplateParametersAndButtonsToActionParameters = (
    actionParameters: IActionParameter[],
    newTemplateName: string
  ) => {
    const selectedTemplate = this.props.sd.whatsappBusinessTemplates.find((t) => t.name === newTemplateName);

    if (selectedTemplate) {
      selectedTemplate.components
        .find((c) => c.type === "HEADER")
        ?.example?.header_text_named_params?.map((p) => {
          const param = this.props.parameters.find((param) => param.name === p.param_name);

          if (!param) {
            const symbol: ISymbol = {
              fixedValue: p.example ?? "",
              currentType: "String",
              cardinality: ECardinality.One,
              category: ESymbolCategory.fixed,
              label: "String",
              description: "String"
            };
            actionParameters.push({
              name: p.param_name,
              symbol,
              cardinality: ECardinality.One,
              Typename: "String",
              isOptionalParameter: false,
              displayName: "HEADER"
            });
          }
        });

      selectedTemplate.components
        .find((c) => c.type === "BODY")
        ?.example?.body_text_named_params?.map((p) => {
          const param = this.props.parameters.find((param) => param.name === p.param_name);

          if (!param) {
            const symbol: ISymbol = {
              fixedValue: p.example ?? "",
              currentType: "String",
              cardinality: ECardinality.One,
              category: ESymbolCategory.fixed,
              label: "String",
              description: "String"
            };
            actionParameters.push({
              name: p.param_name,
              symbol,
              cardinality: ECardinality.One,
              Typename: "String",
              isOptionalParameter: false,
              displayName: "BODY"
            });
          }
        });

      const param = this.props.parameters.find((param) => param.name === "buttons");

      if (!param) {
        const symbol: ISymbol = {
          fixedValue: "",
          currentType: "String",
          cardinality: ECardinality.One,
          category: ESymbolCategory.fixed,
          label: "String",
          description: "String"
        };
        actionParameters.push({
          name: "buttons",
          symbol,
          cardinality: ECardinality.One,
          Typename: "String",
          isOptionalParameter: false
        });

        return;
      }

      const buttonsParam = this.props.parameters.find((param) => param.name === "buttons");
      const buttons: string[] = buttonsParam?.symbol.fixedValue.split(",");
      const newButtons: string[] = [];

      selectedTemplate.components
        .find((c) => c.type === "BUTTONS")
        ?.buttons?.filter((p) => p.type === "QUICK_REPLY")
        .forEach((p) => {
          const button = buttons.find((b) => b.startsWith(`${p.text}_`));
          if (!button) {
            newButtons.push(`${p.text}_`);
          } else {
            newButtons.push(button);
          }
        });

      buttonsParam!.symbol.fixedValue = newButtons.join(",");
    }
  };

  removeWhatsappBusinessTemplateParameters = (actionParameters: IActionParameter[]) => {
    actionParameters = actionParameters.filter(
      (p) =>
        p.name === "buttons" ||
        p.name === "templateName" ||
        p.name === "phoneNumber" ||
        p.name === "allInteractionLinks"
    );

    const buttonsParam = actionParameters.find((param) => param.name === "buttons");
    if (buttonsParam) {
      buttonsParam.symbol.fixedValue = "";
    }

    return actionParameters;
  };

  renderTemplate = () => {
    const { parameters } = this.props;
    const templateNameParameter = parameters.find((p) => p.name === "templateName");

    const dashboardStateIsWorkflow = this.props.sd.dashboardState === DashboardState.workFlow;
    const templateOptions: KuikaSelectboxOption[] = this.props.sd.whatsappBusinessTemplates.map(
      (mt): KuikaSelectboxOption => ({ key: mt.name, value: mt.name, label: mt.name })
    );

    return (
      <>
        <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
          Template Name:
        </span>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            size="sm"
            style={{ width: "100%" }}
            value={templateNameParameter?.symbol?.fixedValue ?? null}
            onChange={(val: string): void => {
              let actionParameters = _.clone(this.props.parameters);
              const templateNameParameterFromParameters = actionParameters.find((p) => p.name === "templateName");

              if (templateNameParameterFromParameters) {
                if (templateNameParameterFromParameters.symbol.fixedValue !== val) {
                  actionParameters = [...this.removeWhatsappBusinessTemplateParameters(actionParameters)];
                }

                templateNameParameterFromParameters.symbol.fixedValue = val;
              } else {
                const symbol: ISymbol = {
                  fixedValue: val,
                  currentType: "String",
                  cardinality: ECardinality.One,
                  category: ESymbolCategory.fixed,
                  label: "String",
                  description: "String"
                };
                actionParameters.push({
                  name: "templateName",
                  symbol,
                  cardinality: ECardinality.One,
                  Typename: "String",
                  isOptionalParameter: false
                });
              }

              this.addWhatsappBusinessTemplateParametersAndButtonsToActionParameters(actionParameters, val);

              this.props.updateParams([...actionParameters]);
            }}
            className="kuika_add_actions_inputs_size"
            options={templateOptions}
          />
        </div>
      </>
    );
  };

  renderButtons = () => {
    const { parameters } = this.props;
    const buttonsParameter = parameters.find((p) => p.name === "buttons");
    const fixedValue = buttonsParameter?.symbol?.fixedValue;

    if (!fixedValue) return null;

    const buttons: string[] = fixedValue.split(",");
    const interactionOptions: KuikaSelectboxOption[] = this.props.interactions.map(
      (ui: IUserInteraction): KuikaSelectboxOption => ({ key: ui.id, value: ui.label, label: ui.label })
    );

    return buttons.map((button) => {
      const buttonName = button.split("_")[0];
      const buttonValue = button.split("_")[1];

      return (
        <div key={buttonName}>
          <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
            {buttonName}
          </span>
          <KuikaSelectbox
            style={{ width: "100%" }}
            placeholder="Select Mail Interaction"
            value={buttonValue ?? null}
            onChange={(value: string): void => {
              const actionParameters = _.clone(this.props.parameters);
              const buttonsParameterFromParameters = actionParameters.find((p) => p.name === "buttons");

              if (buttonsParameterFromParameters) {
                const buttons = buttonsParameterFromParameters.symbol.fixedValue.split(",");
                const buttonIndex = buttons.findIndex((b) => b.startsWith(`${buttonName}_`));
                buttons[buttonIndex] = `${buttonName}_${value}`;
                buttonsParameterFromParameters.symbol.fixedValue = buttons.join(",");
              }

              this.props.updateParams(actionParameters);
            }}
            options={interactionOptions}
          />
        </div>
      );
    });
  };

  render() {
    const selectedWf = this.props.sd.selectedWf;
    const selectedStep = this.props.sd.getSelectedWfStep();

    const phoneNumberParam = this.props.parameters.find((p) => p.name === "phoneNumber");
    const templateNameParam = this.props.parameters.find((p) => p.name === "templateName");
    const buttonsParam = this.props.parameters.find((p) => p.name === "buttons");

    const selectedTemplate = this.props.sd.whatsappBusinessTemplates.find(
      (t) => t.name === templateNameParam?.symbol.fixedValue
    );

    return (
      <>
        {phoneNumberParam && this.renderSymbolParameter(phoneNumberParam)}

        {this.renderTemplate()}

        {selectedTemplate &&
          (selectedTemplate.components.find((c) => c.type === "HEADER")?.example?.header_text_named_params ?? [])
            .length > 0 && (
            <span style={{ marginBottom: 6, marginTop: 16 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
              Header Variables:
            </span>
          )}

        {selectedTemplate &&
          selectedTemplate.components
            .find((c) => c.type === "HEADER")
            ?.example?.header_text_named_params?.map((p) => {
              const param = this.props.parameters.find((param) => param.name === p.param_name);
              return (
                param && (
                  <div className="kuika_property-grid-column" key={p.param_name}>
                    {this.renderSymbolParameter(param)}
                  </div>
                )
              );
            })}

        {selectedTemplate &&
          (selectedTemplate.components.find((c) => c.type === "BODY")?.example?.body_text_named_params ?? []).length >
            0 && (
            <span style={{ marginBottom: 6, marginTop: 16 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
              Body Variables:
            </span>
          )}

        {selectedTemplate &&
          selectedTemplate.components
            .find((c) => c.type === "BODY")
            ?.example?.body_text_named_params?.map((p) => {
              const param = this.props.parameters.find((param) => param.name === p.param_name);
              return (
                param && (
                  <div className="kuika_property-grid-column" key={p.param_name}>
                    {this.renderSymbolParameter(param)}
                  </div>
                )
              );
            })}

        {selectedTemplate && buttonsParam && buttonsParam.symbol?.fixedValue && (
          <>
            <span style={{ marginBottom: 6, marginTop: 16 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
              Buttons:
            </span>

            {(!selectedWf || !selectedStep) && (
              <span className="k_Regular-G1-Left kuika_rightPanelDIName kuika-right-panel-di-name--danger">
                You can only use buttons in Process Automations. Please change the template.
              </span>
            )}

            {selectedWf && selectedStep && selectedStep.assignment.assignmentType !== EWFAssignmentType.external && (
              <span className="k_Regular-G1-Left kuika_rightPanelDIName kuika-right-panel-di-name--danger">
                You don't use buttons in this step. Please select external assignment to use buttons or change the
                template.
              </span>
            )}

            <div className="kuika_property-grid-column">{this.renderButtons()}</div>
          </>
        )}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const sendWhatsappBusinessMessageSymbolsGroup = withContext<ISendWhatsappBusinessMessageSymbolsGroupProps>(
  withSD<ISendWhatsappBusinessMessageSymbolsGroupProps>(SendWhatsappBusinessMessageSymbolsGroup)
);

export { sendWhatsappBusinessMessageSymbolsGroup as SendWhatsappBusinessMessageSymbolsGroup };
